import {Component, inject, OnInit} from '@angular/core';
import { HasPermissionDirective } from '@shared/directives/has-permission.directive';
import {LabelComponent} from '../../../../../../../shared/components/form/label/label.component';
import {FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {CreationButtons} from '../../../../../../../shared/components/ui/creation-buttons/creation-buttons';
import {ErrorService} from '../../../../../../../shared/services/error.service';
import {SnackbarService} from '@shared/services/snackbar.service';
import {ObjetivosService} from '../objetivos.service';
import {alMenosUnObjetivoValidator, objetivoDesdeCheckboxes} from '../objetivos.util';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {CombosService} from '../../../../../../../shared/services/combos.service';
import {ContratoCombo, GenericCombo} from '../../../../../../../shared/services/interfaces/combos.interface';
import {forkJoin} from 'rxjs';
import {ClientesService} from '../../clientes.service';
import {SelectFieldComponent, SelectOption} from '@shared/components/form/select/select-field.component';
import {InputFieldComponent} from '@shared/components/form/input/input-field.component';
import { AppFormComponent } from '@shared/components/ui/list-components';
import { Modal } from '@shared/components/common/modal/modal';

@Component({
  selector: 'app-crear-objetivo',
  imports: [
    HasPermissionDirective,
    LabelComponent,
    FormsModule,
    ReactiveFormsModule,
    CreationButtons,
    SelectFieldComponent,
    InputFieldComponent,
    AppFormComponent,
    Modal,
  ],
  templateUrl: './crear-objetivo.html',
  styles: ``
})
export class CrearObjetivo implements OnInit {

  saveForm!: FormGroup;
  loading = false;
  periodos: GenericCombo[] = [];
  contratos: ContratoCombo[] = [];
  establecimientos: GenericCombo[] = [];

  get periodosOptions(): SelectOption[] { return this.periodos.map(x => ({ value: x.id, label: x.nombre })); }
  get contratosOptions(): SelectOption[] {
    return this.contratos.map(x => ({value: x.id, label: x.nro_contrato || `Contrato #${x.id}`}));
  }
  get establecimientosOptions(): SelectOption[] { return this.establecimientos.map(x => ({ value: x.id, label: x.nombre })); }

  private fB = inject(FormBuilder);
  private objetivosService = inject(ObjetivosService);
  private combosService = inject(CombosService);
  private clientesService = inject(ClientesService);
  private errorService = inject(ErrorService);
  private snackbarService = inject(SnackbarService);
  private dialogRef = inject(MatDialogRef<CrearObjetivo>);
  private clienteId = inject(MAT_DIALOG_DATA);

  ngOnInit(): void {
    this.getCombos();
    this.makeForm();
  }

  private getCombos(): void {
    const periodos = this.combosService.getPeriodosCombo();
    const contratos = this.combosService.getContratosCombo(this.clienteId);
    const establecimientos = this.combosService.getEstablecimientosCombo(this.clienteId);
    const contratoActivo = this.clientesService.getContratoActivo(this.clienteId);

    forkJoin([periodos, contratos, establecimientos, contratoActivo]).subscribe({
      next: (response) => {
        this.periodos = response[0].data;
        this.contratos = response[1].data;
        this.establecimientos = response[2].data;
        this.preseleccionar(response[3].data);
      }, error: (error) => {
        this.errorService.handleError(error);
      }
    });
  }

  /** El objetivo nace con el contrato activo y su establecimiento (o el único que haya). */
  private preseleccionar(contratoActivo: { id: number; cli_establecimiento_id: number | null } | null): void {
    const establecimientoId = contratoActivo?.cli_establecimiento_id
      ?? (this.establecimientos.length === 1 ? this.establecimientos[0].id : null);

    this.saveForm.patchValue({
      ...(contratoActivo ? {cli_contrato_id: contratoActivo.id} : {}),
      ...(establecimientoId ? {cli_establecimiento_id: establecimientoId} : {}),
    });
  }

  private makeForm(): void {
    this.saveForm = this.fB.group({
      cli_cliente_id: [this.clienteId, Validators.required],
      param_periodo_id: ['', Validators.required],
      cli_contrato_id: [''],
      cli_establecimiento_id: [null],
      // El objetivo se edita como dos casillas y se envía como un solo código A/T/X
      aspiracion: [true],
      transferencia: [false],
      cantidad_preneces_proyectadas: ['', [Validators.required, Validators.min(0)]],
      cantidad_donantes: ['', [Validators.required, Validators.min(0)]],
    }, { validators: alMenosUnObjetivoValidator() });
  }

  create(): void {
    this.loading = true;
    const {aspiracion, transferencia, ...resto} = this.saveForm.value;
    const payload = {
      ...resto,
      objetivo: objetivoDesdeCheckboxes(aspiracion, transferencia)!,
    };

    this.objetivosService.store(payload).subscribe({
      next: (response) => {
        if (response.status) {
          this.snackbarService.showSnackBar('Objetivo creado correctamente');
          this.dialogRef.close(true);
        }
      }, error: (error) => {
        this.errorService.handleError(error);
      }
    }).add(() => {
      this.loading = false;
    });
  }

  cancel(): void {
    this.saveForm.reset();
    this.dialogRef.close();
  }
}
